/**
 * Sign-in, switch, and sign-out flows for the saved-account roster.
 * Storage lives in sessionToken.js. These helpers also call the API.
 */

import { fetchOwnProfile, logoutUser } from "../api/api";
import {
  accountHomePath,
  activateAccount,
  clearRoster,
  getRoster,
  rememberAccount,
  removeAccount,
  ROSTER_FULL_MESSAGE,
} from "./sessionToken";

/**
 * Profile-shaped object from a roster row when the network cannot refresh it.
 * @param {import("./sessionToken").SavedAccount} row
 * @returns {Object}
 */
function minimalAccount(row) {
  if (row.type === "organization") {
    return {
      id: row.id,
      type: "organization",
      name: row.name || row.username || "",
      username: row.username || "",
      pfp_url: row.pfp_url || "",
    };
  }
  return {
    id: row.id,
    type: "user",
    first_name: row.name || row.username || "",
    last_name: "",
    username: row.username || "",
    pfp_url: row.pfp_url || "",
  };
}

/**
 * Save a new or existing person/organization session and make it active.
 * A sixth distinct account is rejected and its new token is revoked.
 * @param {Object} account
 * @param {string} token
 * @returns {Promise<{ account?: Object, error?: string }>}
 */
export async function adoptAccount(account, token) {
  const saved = rememberAccount(account, token);
  if (saved.atCapacity) {
    if (token) await logoutUser(account?.id, undefined, token);
    return { error: saved.error || ROSTER_FULL_MESSAGE };
  }
  if (saved.error || !saved.account) return { error: saved.error || "Could not save this account" };
  return { account: saved.account };
}

/**
 * Load one saved account with its own token, then make it active.
 * A rejected token is removed and the current account stays in place.
 * @param {import("./sessionToken").SavedAccount} row
 * @returns {Promise<{ data?: Object, path?: string, rejected?: boolean, error?: string }>}
 */
export async function switchToSavedAccount(row) {
  const result = await fetchOwnProfile(row.type, row.id, row.token);
  if (result.unauthorized) {
    removeAccount(row.key);
    return { rejected: true, error: result.error };
  }
  if (result.error || !result.data) {
    return { error: result.error || "Could not switch accounts" };
  }
  activateAccount(row.key);
  const type = result.data.type || row.type;
  return { data: result.data, path: accountHomePath(type) };
}

/**
 * Revoke the active session and remove it from the roster.
 * The most recently used remaining account becomes active in storage.
 * @returns {Promise<import("./sessionToken").SavedAccount|null>}
 */
export async function signOutAccount() {
  const roster = getRoster();
  const current = roster.accounts.find((row) => row.key === roster.activeKey);
  if (current?.token) await logoutUser(current.id, undefined, current.token);
  if (!current) return null;
  return removeAccount(current.key);
}

/**
 * Revoke every saved session and clear the roster.
 * @returns {Promise<void>}
 */
export async function signOutEveryAccount() {
  const accounts = getRoster().accounts;
  await Promise.all(accounts.map((row) => logoutUser(row.id, undefined, row.token)));
  clearRoster();
}

/**
 * Remove the active account after the server has already revoked it.
 * @returns {import("./sessionToken").SavedAccount|null}
 */
export function dropActiveAccount() {
  const roster = getRoster();
  if (!roster.activeKey) return null;
  return removeAccount(roster.activeKey);
}

/**
 * Use the active saved account, skipping rows whose tokens are rejected.
 * @returns {Promise<{ data?: Object, path?: string, signedOut?: boolean }>}
 */
export async function firstWorkingAccount() {
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const roster = getRoster();
    const row = roster.accounts.find((item) => item.key === roster.activeKey);
    if (!row) {
      clearRoster();
      return { signedOut: true, path: "/" };
    }
    const result = await fetchOwnProfile(row.type, row.id, row.token);
    if (result.unauthorized) {
      removeAccount(row.key);
      continue;
    }
    if (result.error || !result.data) {
      activateAccount(row.key);
      return { data: minimalAccount(row), path: accountHomePath(row.type) };
    }
    activateAccount(row.key);
    return { data: result.data, path: accountHomePath(result.data.type || row.type) };
  }
  clearRoster();
  return { signedOut: true, path: "/" };
}

/**
 * Restore a saved session on startup. Tries the active account, then the others.
 * @param {{ commitToken?: boolean }} [options] When false, do not replace the bearer token
 * @returns {Promise<{ data?: Object, signedOut?: boolean, empty?: boolean }>}
 */
export async function rehydrateSavedAccounts(options = {}) {
  const commitToken = options.commitToken !== false;
  const roster = getRoster();
  const ordered = [...roster.accounts].sort((a, b) => {
    if (a.key === roster.activeKey) return -1;
    if (b.key === roster.activeKey) return 1;
    return (b.lastUsed || 0) - (a.lastUsed || 0);
  });
  if (!ordered.length) return { empty: true };

  for (const row of ordered) {
    const result = await fetchOwnProfile(row.type, row.id, row.token);
    if (result.unauthorized) {
      removeAccount(row.key, { commitToken });
      continue;
    }
    if (result.error || !result.data) {
      activateAccount(row.key, { commitToken });
      return { data: minimalAccount(row) };
    }
    activateAccount(row.key, { commitToken });
    return { data: result.data };
  }
  if (commitToken) clearRoster();
  return { signedOut: true };
}
